import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import {
  AHSeparator,
  AHText,
  Box,
  EmployeeAvatar,
  ListItem,
  useBoxStyle,
  useTheme,
} from "arkh";
import React, { useLayoutEffect, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { SignalOpenedByEmployeeQuery } from "./__generated__/SignalOpenedByEmployeeQuery.graphql";
import { Platform, SectionList } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useSizeClass } from "@/src/common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { FormattedMessage } from "react-intl";
import { FormattedDate } from "@/src/common/dates/FormattedDate";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import { UserLoggedInNavigatorScreenProps } from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { TimeZoneProvider } from "@/src/OS/Timezone";
import { StoreBanner } from "@/src/components/OrgHeaders/StoreBanner";
import { Header } from "@react-navigation/elements";
import { HeaderButton } from "@/src/components";

import { onlyUnique } from "@/src/utils";

function InnerSignalOpenedByEmployeeScreen(props: {
  signalId: string;
  fetchKey: string;
}) {
  const data = useLazyLoadQuery<SignalOpenedByEmployeeQuery>(
    graphql`
      query SignalOpenedByEmployeeQuery($signalId: ID!) {
        signal(id: $signalId) {
          id
          employeeReceiverReceipts {
            id
            actor {
              ... on GroupActor {
                __typename
                employee {
                  id
                  name
                  ...Avatar_employee
                }
              }
              ... on UserActor {
                __typename
                storeUser {
                  id
                  employee {
                    id
                    name
                    ...Avatar_employee
                  }
                }
              }
            }
            openedAtDate {
              ...FormattedDate
            }
          }
          store {
            employees {
              edges {
                node {
                  id
                  name
                  ...Avatar_employee
                }
              }
            }
          }
        }
      }
    `,
    {
      signalId: props.signalId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const intl_tokens = useIntlTokens();

  const opened = useMemo(
    () =>
      data.signal.employeeReceiverReceipts
        .flatMap((receipt) => {
          if (
            receipt.actor?.__typename === "GroupActor" &&
            receipt.actor.employee
          ) {
            return [
              {
                receiptId: receipt.id,
                ...receipt.actor.employee,
                openedAtDate: receipt.openedAtDate,
              },
            ];
          }
          if (
            receipt.actor?.__typename === "UserActor" &&
            receipt.actor.storeUser?.employee
          ) {
            return [
              {
                receiptId: receipt.id,
                ...receipt.actor.storeUser.employee,
                openedAtDate: receipt.openedAtDate,
              },
            ];
          }
          return [];
        })
        .reverse()
        .filter((r, index, arr) =>
          onlyUnique(
            r.id,
            index,
            arr.map((r) => r.id)
          )
        )
        .reverse(),
    [data.signal.employeeReceiverReceipts]
  );

  const unread = useMemo(
    () =>
      data.signal.store.employees?.edges
        .flatMap((receipt) =>
          receipt.node
            ? ([
                {
                  ...receipt.node,
                  openedAtDate: undefined,
                },
              ] as const)
            : []
        )
        .filter((u) => opened.findIndex((_u) => u.id === _u.id) === -1) ?? [],
    [data.signal.store.employees?.edges, opened]
  );

  type Item = (typeof opened)[number] | (typeof unread)[number];
  const sections = useMemo(() => {
    return [
      ...(unread.length > 0
        ? [
            {
              key: "unread" as const,
              title: intl_tokens.Unread,
              data: unread as Item[],
              receiptIds: unread.map((u) => u.id),
            },
          ]
        : []),
      ...(opened.length > 0
        ? [
            {
              key: "opened" as const,
              title: intl_tokens.Opened,
              data: opened as Item[],
              receiptIds: opened.map((u) => u.receiptId),
            },
          ]
        : []),
    ];
  }, [intl_tokens.Unread, intl_tokens.Opened, unread, opened]);

  const safeAreaInsets = useSafeAreaInsets();
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  const insets = isCompact
    ? {
        bottom: safeAreaInsets.bottom,
      }
    : {};
  const contentBox = useBoxStyle({
    spaceHorizontal: "medium",
    spaceBottom: "medium",
    insets,
  });

  return (
    <SectionList
      contentContainerStyle={contentBox}
      sections={sections}
      stickySectionHeadersEnabled={false}
      renderItem={({ item, index, section }) => {
        return (
          <ListItem
            disabled
            border="border-primary"
            isFirst={index === 0}
            isLast={index === section.data.length - 1}
            left={<EmployeeAvatar employee={item} size="large" />}
            right={
              item.openedAtDate ? (
                <AHText type="detail">
                  <FormattedMessage
                    defaultMessage="Opened at {openedAtFormatted}"
                    description="SignalOpenedByScreen openedAt list item text"
                    values={{
                      openedAtFormatted: (
                        <FormattedDate date={item.openedAtDate} />
                      ),
                    }}
                  />
                </AHText>
              ) : null
            }
          >
            <ListItem.Title>{item.name}</ListItem.Title>
          </ListItem>
        );
      }}
      ItemSeparatorComponent={AHSeparator}
      renderSectionHeader={({ section }) => {
        return (
          <AHText
            spaceHorizontal="medium"
            spaceTop="medium"
            spaceBottom="small"
          >
            {section.title}
          </AHText>
        );
      }}
    />
  );
}

export const SignalOpenedByEmployeeScreen = withWebModalStack(
  (props: UserLoggedInNavigatorScreenProps<"SignalOpenedByEmployee">) => {
    const backgroundColor = useTheme()["surface-background-light"];
    useLayoutEffect(() => {
      props.navigation.setOptions({
        headerShown: false,
        contentStyle: {
          backgroundColor,
        },
      });
    }, [props.navigation, backgroundColor]);
    return (
      <TimeZoneProvider timezone={props.route.params.storeTimezone}>
        {props.route.params.storeName ? (
          <StoreBanner storeName={props.route.params.storeName} />
        ) : null}
        <Box spaceHorizontal={Platform.OS === "web" ? "none" : "medium"}>
          <Header
            headerStatusBarHeight={
              Platform.OS === "android" && props.route.params.storeName
                ? undefined
                : 4
            }
            headerLeft={() => (
              <HeaderButton type="CLOSE" onPress={props.navigation.goBack} />
            )}
            title=""
          />
        </Box>
        <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
          {(fetchKey) => (
            <InnerSignalOpenedByEmployeeScreen
              signalId={props.route.params.signalId}
              fetchKey={fetchKey}
            />
          )}
        </LazyQueryNetworkBoundrary>
      </TimeZoneProvider>
    );
  }
);
